import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import CareProcessPage from "@/pages/CareProcessPage";
import WhyChoosePage from "@/pages/WhyChoosePage";
import ContactPage from "@/pages/ContactPage";
import RatesPage from "@/pages/RatesPage";
import ReferralPage from "@/pages/ReferralPage";
import AdminPage from "@/pages/AdminPage";
import PrivacyPage from "@/pages/PrivacyPage";
import { AuthProvider, RequireRole } from "@/portal/AuthContext";
import Login from "@/portal/Login";
import ForgotPassword from "@/portal/ForgotPassword";
import ResetPassword from "@/portal/ResetPassword";
import { AdminLayout } from "@/portal/admin/AdminLayout";
import AdminDashboard from "@/portal/admin/Dashboard";
import AdminReferrals from "@/portal/admin/Referrals";
import AdminReferralDetail from "@/portal/admin/ReferralDetail";
import AdminPatients from "@/portal/admin/Patients";
import AdminPatientDetail from "@/portal/admin/PatientDetail";
import AdminConsultations from "@/portal/admin/Consultations";
import AdminConsultationDetail from "@/portal/admin/ConsultationDetail";
import AdminCaregivers from "@/portal/admin/Caregivers";
import AdminSchedule from "@/portal/admin/Schedule";
import AdminSettings from "@/portal/admin/Settings";
import { CaregiverLayout } from "@/portal/caregiver/CaregiverLayout";
import CaregiverDashboard from "@/portal/caregiver/CaregiverDashboard";
import MySchedule from "@/portal/caregiver/MySchedule";
import UpcomingVisits from "@/portal/caregiver/UpcomingVisits";
import VisitDetail from "@/portal/caregiver/VisitDetail";
import CaregiverProfile from "@/portal/caregiver/CaregiverProfile";
import { PatientLayout } from "@/portal/patient/PatientLayout";
import PatientDashboard from "@/portal/patient/PatientDashboard";
import PatientVisits from "@/portal/patient/PatientVisits";
import PatientCarePlan from "@/portal/patient/PatientCarePlan";
import PatientProfile from "@/portal/patient/PatientProfile";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollManager />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services-and-rates" element={<RatesPage />} />
            <Route path="/care-process" element={<CareProcessPage />} />
            <Route path="/why-choose-us" element={<WhyChoosePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/referral" element={<ReferralPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/portal/login" element={<Login role="admin" />} />
            <Route path="/portal/forgot-password" element={<ForgotPassword portal="admin" />} />
            <Route path="/caregiver/login" element={<Login role="caregiver" />} />
            <Route path="/caregiver/forgot-password" element={<ForgotPassword portal="caregiver" />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portal" element={<RequireRole role="admin"><AdminLayout /></RequireRole>}>
              <Route index element={<AdminDashboard />} />
              <Route path="referrals" element={<AdminReferrals />} />
              <Route path="referrals/:id" element={<AdminReferralDetail />} />
              <Route path="consultations" element={<AdminConsultations />} />
              <Route path="consultations/:id" element={<AdminConsultationDetail />} />
              <Route path="patients" element={<AdminPatients />} />
              <Route path="patients/:id" element={<AdminPatientDetail />} />
              <Route path="caregivers" element={<AdminCaregivers />} />
              <Route path="schedule" element={<AdminSchedule />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            <Route path="/caregiver" element={<RequireRole role="caregiver"><CaregiverLayout /></RequireRole>}>
              <Route index element={<CaregiverDashboard />} />
              <Route path="schedule" element={<MySchedule />} />
              <Route path="visits" element={<UpcomingVisits />} />
              <Route path="visits/:id" element={<VisitDetail />} />
              <Route path="profile" element={<CaregiverProfile />} />
            </Route>
            <Route path="/patient/login" element={<Login role="patient" />} />
            <Route path="/patient/forgot-password" element={<ForgotPassword portal="patient" />} />
            <Route path="/patient" element={<RequireRole role="patient"><PatientLayout /></RequireRole>}>
              <Route index element={<PatientDashboard />} />
              <Route path="visits" element={<PatientVisits />} />
              <Route path="care-plan" element={<PatientCarePlan />} />
              <Route path="profile" element={<PatientProfile />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
